import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { SectionCard, StatCard, StatGrid } from "../../components/admin/AdminCard";
import LoginAsButton from "../../components/LoginAsButton";
import { getAthlete360, money, type Athlete360, type Period } from "../../services/club360";
import { formatDate } from "../../lib/dateFormat";

/**
 * Viziunea 360 a unui sportiv, din partea clubului.
 *
 * Its own page rather than a tab on the platform's user screen: a club admin
 * opening an athlete wants the club's facts — turnout, fees, plan, who brought
 * them in, who to ring — not the platform's view of a user account. Scoped to
 * the active club, so an athlete who trains at two clubs does not show one
 * club the other's business.
 */

const PERIODS: { key: Period; label: string }[] = [
  { key: "month", label: "Luna curentă" },
  { key: "quarter", label: "Ultimele 3 luni" },
  { key: "all", label: "Tot timpul" },
];

const MARK: Record<string, { label: string; cls: string }> = {
  present: { label: "prezent", cls: "text-emerald-700" },
  late: { label: "întârziat", cls: "text-emerald-600" },
  absent: { label: "absent", cls: "text-rose-600" },
  excused: { label: "motivat", cls: "text-amber-600" },
};

export default function ClubAthleteDetails() {
  const { athleteId } = useParams<{ athleteId: string }>();
  const navigate = useNavigate();
  const { activeClubId } = useAuth();

  const [data, setData] = useState<Athlete360 | null>(null);
  const [period, setPeriod] = useState<Period>("quarter");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!athleteId) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getAthlete360(athleteId, { clubId: activeClubId, period }));
    } catch (e: any) {
      setError(e?.response?.data?.error || "Nu am putut încărca sportivul.");
      setData(null);
    }
    setLoading(false);
  }, [athleteId, activeClubId, period]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return (
      <ClubAdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 rounded-full border-indigo-600 animate-spin border-t-transparent" />
        </div>
      </ClubAdminLayout>
    );
  }

  if (error || !data) {
    return (
      <ClubAdminLayout>
        <div className="p-8 text-center bg-white border rounded-2xl border-slate-200">
          <p className="font-medium text-slate-700">{error || "Sportivul nu există."}</p>
          <button onClick={() => navigate(-1)} className="mt-3 text-sm font-semibold text-indigo-600">
            Înapoi
          </button>
        </div>
      </ClubAdminLayout>
    );
  }

  const a = data.athlete;
  const marked = data.attendance.present + data.attendance.absent + data.attendance.excused;
  const initials = (a.full_name || a.email).substring(0, 2).toUpperCase();

  return (
    <ClubAdminLayout>
      <div className="mx-auto max-w-[1500px] space-y-5">
        <AdminPageHeader
          bareIcon
          title={a.full_name || a.email}
          subtitle={
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-1.5">
                {a.age !== null && (
                  <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-700">
                    {a.age} ani
                  </span>
                )}
                {a.gender && (
                  <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-700">
                    {a.gender === "male" ? "băiat" : a.gender === "female" ? "fată" : "altul"}
                  </span>
                )}
                {a.status && a.status !== "active" && (
                  <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-rose-100 text-rose-700">
                    {a.status}
                  </span>
                )}
                {a.email_verified === false && (
                  <span
                    className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-700"
                    title="Nu și-a confirmat emailul, deci nu a intrat niciodată în cont."
                  >
                    neconfirmat
                  </span>
                )}
                {a.vulnerable && (
                  <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-violet-100 text-violet-700">
                    situație specială
                  </span>
                )}
              </div>
              <p className="text-sm break-all text-slate-500">
                {a.email}
                {a.username ? ` · ${a.username}` : ""}
                {a.phone ? ` · ${a.phone}` : ""}
              </p>
              <p className="text-xs text-slate-400">
                În club de la {formatDate(a.joined_at)}
                {a.city ? ` · ${a.city}` : ""}
              </p>
            </div>
          }
          icon={
            a.profile_url ? (
              <img src={a.profile_url} alt="" className="object-cover w-16 h-16 shadow-lg sm:w-20 sm:h-20 rounded-2xl" />
            ) : (
              <div className="flex items-center justify-center w-16 h-16 text-xl font-bold text-white shadow-lg sm:w-20 sm:h-20 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-2xl sm:text-2xl">
                {initials}
              </div>
            )
          }
          actions={
            <div className="flex items-center gap-2">
              <LoginAsButton userId={a.id} />
              <AdminButton variant="secondary" onClick={() => navigate(-1)}>
                Înapoi
              </AdminButton>
            </div>
          }
        />

        <div className="flex gap-1 p-1 border rounded-xl border-slate-200 bg-slate-100 w-fit">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
                period === p.key ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <StatGrid>
          <StatCard
            label="Prezență"
            value={marked === 0 ? "—" : `${data.attendance.rate}%`}
            dotClass={
              data.attendance.rate >= 75 ? "bg-emerald-500" : data.attendance.rate >= 50 ? "bg-amber-500" : "bg-rose-500"
            }
            valueClass={marked === 0 ? "text-slate-400" : "text-slate-800"}
            hint={
              marked === 0
                ? "nicio prezență pontată"
                : `${data.attendance.present} din ${marked} pontate${data.attendance.late > 0 ? ` · ${data.attendance.late} întârzieri` : ""}`
            }
            hintTone={marked === 0 ? "down" : "muted"}
          />
          <StatCard
            label="Puncte"
            value={data.points.total}
            dotClass="bg-amber-500"
            hint={
              data.points.club_athletes > 0
                ? `locul ${data.points.club_rank} din ${data.points.club_athletes} în club`
                : "—"
            }
            hintTone="muted"
          />
          <StatCard
            label="Plan"
            value={data.plan?.paid ? data.plan.plan_code : "free"}
            valueClass={data.plan?.paid ? "text-violet-700 capitalize" : "text-slate-500"}
            dotClass={data.plan?.paid ? "bg-violet-500" : "bg-slate-300"}
            hint={
              data.plan?.paid
                ? data.plan.period_end
                  ? `până la ${formatDate(data.plan.period_end)}`
                  : data.plan.status
                : "fără upgrade"
            }
            hintTone={data.plan?.paid ? "up" : "muted"}
          />
          <StatCard
            label="Taxe"
            value={data.payment ? money(data.payment.outstanding_cents) : "—"}
            dotClass={
              data.payment?.status === "overdue"
                ? "bg-rose-500"
                : data.payment?.status === "pending"
                  ? "bg-amber-500"
                  : "bg-emerald-500"
            }
            valueClass={data.payment?.status === "overdue" ? "text-rose-600" : "text-slate-800"}
            hint={
              !data.payment || data.payment.status === "none"
                ? "nicio taxă alocată"
                : data.payment.status === "overdue"
                  ? "restanță"
                  : data.payment.status === "pending"
                    ? data.payment.next_due_date
                      ? `scadent ${formatDate(data.payment.next_due_date)}`
                      : "de încasat"
                    : "la zi"
            }
            hintTone={data.payment?.status === "overdue" ? "down" : "muted"}
          />
        </StatGrid>

        <div className="grid gap-5 lg:grid-cols-2">
          <SectionCard title="Echipe și antrenori" bodyPadded>
            {data.teams.length === 0 ? (
              <p className="text-sm text-rose-600">
                Nu e în nicio echipă — nu apare la nicio prezență și la niciun antrenament.
              </p>
            ) : (
              <ul className="space-y-3">
                {data.teams.map((t) => (
                  <li key={t.id} className="pb-3 border-b border-slate-100 last:border-0 last:pb-0">
                    <button
                      onClick={() => navigate(`/dashboard/admin-club/teams/${t.id}`)}
                      className="font-semibold text-left text-slate-800 hover:text-indigo-600"
                    >
                      {t.name}
                    </button>
                    <span className="ml-2 text-xs text-slate-400">
                      {[t.age_group, t.sport].filter(Boolean).join(" · ")}
                      {t.is_primary ? " · principală" : ""}
                    </span>
                    <p className="mt-0.5 text-sm text-slate-600">
                      {t.coach ? (
                        <>
                          Antrenor:{" "}
                          <button
                            onClick={() => navigate(`/admin/users/${t.coach!.id}`)}
                            className="font-medium hover:text-indigo-600"
                          >
                            {t.coach.full_name || t.coach.email}
                          </button>
                          {t.coach.phone ? ` · ${t.coach.phone}` : ""}
                        </>
                      ) : (
                        <span className="text-amber-700">Echipa nu are antrenor alocat.</span>
                      )}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          <SectionCard title="Părinți și contact" bodyPadded>
            {data.parents.length === 0 ? (
              <p className="text-sm text-slate-500">
                Niciun părinte legat de cont.
                {!a.phone && " Nici sportivul nu are telefon — nimeni de contactat."}
              </p>
            ) : (
              <ul className="space-y-2">
                {data.parents.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center gap-2 text-sm">
                    <button
                      onClick={() => navigate(`/admin/users/${p.id}`)}
                      className="font-medium text-slate-800 hover:text-indigo-600"
                    >
                      {p.full_name || p.email}
                    </button>
                    {p.phone ? (
                      <a href={`tel:${p.phone}`} className="text-indigo-600 hover:underline">
                        {p.phone}
                      </a>
                    ) : (
                      <span className="text-[11px] text-amber-600">fără telefon</span>
                    )}
                    <span className="text-xs break-all text-slate-400">{p.email}</span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          <SectionCard title="Afiliere" bodyPadded>
            {!data.referral ? (
              <p className="text-sm text-slate-500">Nu a venit prin cod de afiliere.</p>
            ) : (
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-slate-500">Adus prin</dt>
                  <dd className="font-semibold text-slate-800">
                    {data.referral.source === "club"
                      ? "codul clubului"
                      : data.referral.source === "coach"
                        ? `antrenor${data.referral.by_name ? `: ${data.referral.by_name}` : ""}`
                        : data.referral.source}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Conversie</dt>
                  <dd className={`font-semibold ${data.referral.converted ? "text-emerald-700" : "text-slate-600"}`}>
                    {data.referral.converted
                      ? data.referral.converted_plan || "convertit"
                      : "încă nu a făcut upgrade"}
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-slate-500">Câștigul clubului</dt>
                  <dd className="font-semibold text-emerald-700">
                    {money(data.referral.club_earned_cents)}
                    {data.referral.source === "coach" && (
                      <span className="ml-1 text-[11px] font-normal text-slate-400">
                        (venit prin antrenor, dar acumulat de club)
                      </span>
                    )}
                  </dd>
                </div>
              </dl>
            )}
          </SectionCard>

          <SectionCard title="Taxe și servicii" bodyPadded>
            {!data.payment || data.payment.status === "none" ? (
              <p className="text-sm text-slate-500">
                Nicio taxă alocată acestui sportiv la clubul tău.
              </p>
            ) : (
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-slate-500">Valoare lunară</dt>
                  <dd className="font-semibold text-slate-800">{money(data.payment.billed_cents)}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">De încasat</dt>
                  <dd
                    className={`font-semibold ${
                      data.payment.outstanding_cents > 0 ? "text-amber-600" : "text-slate-800"
                    }`}
                  >
                    {money(data.payment.outstanding_cents)}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Încasat luna asta</dt>
                  <dd className="font-semibold text-emerald-700">{money(data.payment.collected_cents)}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Scadența următoare</dt>
                  <dd className="font-semibold text-slate-800">
                    {data.payment.next_due_date ? formatDate(data.payment.next_due_date) : "—"}
                  </dd>
                </div>
              </dl>
            )}
          </SectionCard>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          <SectionCard title="Ultimele antrenamente" bodyPadded>
            {data.attendance.recent.length === 0 ? (
              <p className="text-sm text-slate-500">Nicio sesiune în perioada aleasă.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.attendance.recent.map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <span className="min-w-0">
                      <span className="block truncate text-slate-700">{s.title}</span>
                      <span className="block text-[11px] text-slate-400">{formatDate(s.start_at)}</span>
                    </span>
                    <span
                      className={`font-semibold whitespace-nowrap ${
                        s.status ? MARK[s.status]?.cls || "text-slate-500" : "text-slate-300"
                      }`}
                    >
                      {/* Nothing recorded is not an absence — the coach never
                          took the register for that session. */}
                      {s.status ? MARK[s.status]?.label || s.status : "nepontat"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          <SectionCard title="Puncte recente" bodyPadded>
            {data.points.recent.length === 0 ? (
              <p className="text-sm text-slate-500">Niciun punct acordat încă.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.points.recent.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <span className="min-w-0">
                      <span className="block truncate text-slate-700">{p.note || p.source}</span>
                      <span className="block text-[11px] text-slate-400">{formatDate(p.created_at)}</span>
                    </span>
                    <span
                      className={`font-bold tabular-nums ${p.delta >= 0 ? "text-emerald-700" : "text-rose-600"}`}
                    >
                      {p.delta >= 0 ? `+${p.delta}` : p.delta}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>
      </div>
    </ClubAdminLayout>
  );
}
